import React from 'react';
import { toast } from 'react-toastify';
import { DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, IDropdownOption, IconButton, Label, MessageBar, MessageBarType, Panel, PanelType, Pivot, PivotItem, PrimaryButton, Separator, Spinner, SpinnerSize, Stack, StackItem } from '@fluentui/react';
import { useCreateCrmPurchaseOrderMutation, useFetchProcurementOrderFromVendorMutation, useDeleteProcurementOrderMutation, useGenerateProcurementOrderCsvMutation, useGetProcurementOrderQuery, useGetProcurementOrderQuestionsQuery, usePlaceProcurementOrderMutation, useUpdateProcurementOrderMutation, useUpdateProcurementOrderStatusMutation } from '../../../store/endpoints/ProcurementEndpoints';
import { useLazyResolveRecordUrlQuery } from '../../../store/endpoints/RecordLinksEndpoints';
import { useGetInstalledExtensionsQuery } from '../../../store/Api';
import { EXTENSION_TYPE_PROCUREMENT } from '../../../models/Extension';
import { ICrmPurchaseOrderReference, IGetProcurementQuestionResponse, IProcurementOrder, IProcurementQuestion, IProcurementRequirementAnswer, IUpdateProcurementOrderRequest } from '../../../models/Procurement';
import OrderItemsTable from './ExistingOrderPanel/OrderItemsTable';
import OrderActivities from './ExistingOrderPanel/OrderActivities';
import DataField from '../../Common/DataField';
import AccordionBox from '../../Common/AccordionBox';
import CompanyForm from '../ProcureProduct/CompanyForm';
import { debounce } from 'lodash';
import ToggleField from '../../Common/ToggleField';
import { ORDER_STATUSES } from '../../../models/Order';

interface IExistingOrderPanelProps {
    orderId?: string;
    onClose: () => void;
}

const STEP_NONE = 'None';
const STEP_ORDER_INFO = 'Order Information';
const STEP_PRODUCTS = 'Products';
const STEP_VENDOR_QUESTIONS = 'Vendor Questions';
const STEP_END_USER = 'End User';
const STEP_FULFILLMENT = 'Ship To';
const STEP_REVIEW = 'Review';

// Toast body for CRM purchase order creation: the headline plus, when present, a bullet per item that was
// left off the PO and why.
const renderCrmPoToast = (message: string, warnings: string[]) => (
    <div>
        <div>{message}</div>
        {warnings.length > 0 && (
            <ul style={{ margin: '0.5em 0 0', paddingLeft: '1.25em' }}>
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
        )}
    </div>
);

const ExistingOrderPanel: React.FC<IExistingOrderPanelProps> = (props) => {
    const orderData = useGetProcurementOrderQuery({ orderId: props.orderId ?? '' }, { skip: !props.orderId });
    const installedExtensions = useGetInstalledExtensionsQuery();
    const [order, setOrder] = React.useState<IProcurementOrder | undefined>(undefined);
    const [currentStep, setCurrentStep] = React.useState<string>(STEP_ORDER_INFO);
    const questions = useGetProcurementOrderQuestionsQuery({orderId: props.orderId ?? ''}, {skip: !props.orderId});
    const [itemAnswers, setItemAnswers] = React.useState<{[itemId: string]: {[questionId: string]: string}}>({});
    const [procure] = usePlaceProcurementOrderMutation();
    const [ordering, setOrdering] = React.useState(false);
    const [orderError, setOrderError] = React.useState<string | undefined>();
    const [updateOrder] = useUpdateProcurementOrderMutation();
    const [updating, setUpdating] = React.useState(false);
    const [updateOrderData, setUpdateOrderData] = React.useState<IUpdateProcurementOrderRequest>({});
    const [updateOrderError, setUpdateOrderError] = React.useState<string | undefined>();
    const [generateCsv] = useGenerateProcurementOrderCsvMutation();
    const [csvUrl, setCsvUrl] = React.useState<string | undefined>();
    const [generatingCsv, setGeneratingCsv] = React.useState(false);
    const [csvError, setCsvError] = React.useState<string | undefined>();
    const [updateOrderStatus] = useUpdateProcurementOrderStatusMutation();
    const [updatingStatus, setUpdatingStatus] = React.useState(false);
    const [statusError, setStatusError] = React.useState<string | undefined>();
    const [fetchFromVendor] = useFetchProcurementOrderFromVendorMutation();
    const [fetchingFromVendor, setFetchingFromVendor] = React.useState(false);
    const [fetchFromVendorError, setFetchFromVendorError] = React.useState<string | undefined>();
    const [fetchFromVendorSuccess, setFetchFromVendorSuccess] = React.useState(false);
    const [deleteOrder] = useDeleteProcurementOrderMutation();
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string | undefined>();
    const [createCrmPurchaseOrder] = useCreateCrmPurchaseOrderMutation();
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();
    const [creatingCrmPo, setCreatingCrmPo] = React.useState(false);

    const hasProcurementExtension = React.useMemo(() =>
        !!(order?.vendor && installedExtensions.data?.some(
            ext => ext.extensionType === EXTENSION_TYPE_PROCUREMENT &&
                ext.vendor?.toLowerCase() === order.vendor?.toLowerCase()
        )),
        [order?.vendor, installedExtensions.data]
    );

    const statusOptions: IDropdownOption[] = Object.values(ORDER_STATUSES).map(s => ({ key: s, text: s }));

    const [needOrderInfo, setNeedOrderInfo] = React.useState(false);
    const [needOrderInfoMessage, setNeedOrderInfoMessage] = React.useState<string | undefined>();
    const [needVendorQuestions, setNeedVendorQuestions] = React.useState(false);
    const [needVendorQuestionsMessage, setNeedVendorQuestionsMessage] = React.useState<string | undefined>();
    const [warnVendorQuestions, setWarnVendorQuestions] = React.useState(false);
    const [needEndUser, setNeedEndUser] = React.useState(false);
    const [needEndUserMessage, setNeedEndUserMessage] = React.useState<string | undefined>();
    const [needFulfillment, setNeedFulfillment] = React.useState(false);
    const [needFulfillmentMessage, setNeedFulfillmentMessage] = React.useState<string | undefined>();
    const [orderable, setOrderable] = React.useState(false);
    const [ordered, setOrdered] = React.useState(false);

    React.useEffect(() => {
        setCsvUrl(undefined);
        setCsvError(undefined);
        setStatusError(undefined);
        setFetchFromVendorError(undefined);
        setFetchFromVendorSuccess(false);
        setDeleteError(undefined);
        setUpdateOrderData({}); // reset unsaved changes when switching orders
    }, [props.orderId]);

    const saveRef = React.useRef<() => void>(null!);
    saveRef.current = () => {
        if (!order) return;
        setUpdating(true);
        const itemAnswersList = Object.entries(itemAnswers).map(([itemId, questionAnswers]) => ({
            itemId,
            answers: Object.entries(questionAnswers).map(([questionId, value]) => ({
                id: questionId,
                value
            }))
        }));
        updateOrder({
            orderId: order.id,
            order: {
                ...updateOrderData,
                itemAnswers: itemAnswersList
            }
        }).unwrap().then(() => {
            setUpdateOrderError(undefined);
        }).catch((err) => {
            setUpdateOrderError(err?.data?.message || 'Error updating order');
        }).finally(() => {
            setUpdating(false);
        });
    };

    const debouncedSave = React.useRef(debounce(() => saveRef.current(), 1000)).current;

    React.useEffect(() => {
        return () => {
            debouncedSave.cancel();
        }
    }, [props.orderId, debouncedSave]);

    const handleGenerateCsv = () => {
        if (!order) return;
        setGeneratingCsv(true);
        setCsvError(undefined);
        setCsvUrl(undefined);
        generateCsv({ orderId: order.id })
            .unwrap()
            .then((res) => {
                if (res.success && res.url) {
                    setCsvUrl(res.url);
                } else {
                    setCsvError(res.error || 'Failed to generate CSV');
                }
            })
            .catch(() => setCsvError('Failed to generate CSV'))
            .finally(() => setGeneratingCsv(false));
    };

    const handleFetchFromVendor = () => {
        if (!order) return;
        setFetchingFromVendor(true);
        setFetchFromVendorError(undefined);
        setFetchFromVendorSuccess(false);
        fetchFromVendor({ orderId: order.id })
            .unwrap()
            .then((res) => {
                if (res.success) {
                    setFetchFromVendorSuccess(true);
                    orderData.refetch();
                } else {
                    setFetchFromVendorError(res.message || 'Failed to fetch order from vendor');
                }
            })
            .catch(() => setFetchFromVendorError('Failed to fetch order from vendor'))
            .finally(() => setFetchingFromVendor(false));
    };

    const handleDeleteOrder = () => {
        if (!order) return;
        setDeleting(true);
        setDeleteError(undefined);
        deleteOrder({ orderId: order.id })
            .unwrap()
            .then(() => {
                setShowDeleteConfirm(false);
                props.onClose();
            })
            .catch(() => setDeleteError('Failed to delete order'))
            .finally(() => setDeleting(false));
    };

    const handleCreateCrmPurchaseOrder = () => {
        if (!order) return;
        setCreatingCrmPo(true);
        const toastId = toast.loading('Creating CRM purchase order…');
        createCrmPurchaseOrder({ orderId: order.id })
            .unwrap()
            .then((res) => {
                const warnings = res.warnings ?? [];
                if (res.success) {
                    toast.update(toastId, {
                        type: warnings.length > 0 ? 'warning' : 'success',
                        render: renderCrmPoToast('CRM purchase order created.', warnings),
                        isLoading: false,
                        // Keep warnings up until dismissed so the user can read why items were left off.
                        autoClose: warnings.length > 0 ? false : 3000,
                        // The loading toast starts non-dismissable; re-enable closing once it resolves.
                        closeButton: true,
                        closeOnClick: true,
                    });
                    orderData.refetch();
                } else {
                    toast.update(toastId, {
                        type: 'error',
                        render: renderCrmPoToast(res.message || 'Failed to create CRM purchase order', warnings),
                        isLoading: false,
                        autoClose: warnings.length > 0 ? false : 4000,
                        closeButton: true,
                        closeOnClick: true,
                    });
                }
            })
            .catch(() => toast.update(toastId, { type: 'error', render: 'Failed to create CRM purchase order', isLoading: false, autoClose: 4000, closeButton: true, closeOnClick: true }))
            .finally(() => setCreatingCrmPo(false));
    };

    const handleOpenCrmPurchaseOrder = async (poRef: ICrmPurchaseOrderReference) => {
        try {
            const res = await resolveRecordUrl({ folioId: poRef.folioId, recordType: 'PurchaseOrder', recordId: poRef.purchaseOrderId }).unwrap();
            if (res.success && res.url) {
                window.open(res.url, '_blank', 'noopener');
            } else {
                toast.error(res.error || `${poRef.folioName ?? 'This CRM'} does not support opening purchase orders`);
            }
        } catch {
            toast.error('Failed to open CRM purchase order');
        }
    };

    const handleStatusChange = (_: React.FormEvent, option?: IDropdownOption) => {
        if (!order || !option) return;
        const newStatus = option.key as string;
        setUpdatingStatus(true);
        setStatusError(undefined);
        updateOrderStatus({ orderId: order.id, status: newStatus })
            .unwrap()
            .then((res) => {
                if (res.success) {
                    setOrder(prev => prev ? { ...prev, status: newStatus } : prev);
                } else {
                    setStatusError(res.message || 'Failed to update status');
                }
            })
            .catch(() => setStatusError('Failed to update status'))
            .finally(() => setUpdatingStatus(false));
    };

    React.useEffect(() => {
        if (!props.orderId) {
            setOrder(undefined);
        } else if (orderData.data) {
            setOrder(orderData.data);
            const initialAnswers: {[itemId: string]: {[questionId: string]: string}} = {};
            orderData.data.items.forEach(item => {
                initialAnswers[item.id] = {};
                item.answers?.forEach(answer => {
                    initialAnswers[item.id][answer.id] = answer.value ?? '';
                });
            });
            setItemAnswers(initialAnswers);
        } else {
            setOrder(undefined);
        }
    }, [orderData.data]);

    React.useEffect(() => {
        if (order && questions.data) {
            setNeedOrderInfo(!order.name);
            setNeedOrderInfoMessage(!order.name ? 'Missing required information' : undefined);

            const vendorQuestionStatus = validateVendorQuestions();
            setNeedVendorQuestions(vendorQuestionStatus === 'error');
            setNeedVendorQuestionsMessage(vendorQuestionStatus === 'error' ? 'Missing required information' : vendorQuestionStatus === 'warning' ? 'Missing some non-required information' : undefined);
            setWarnVendorQuestions(vendorQuestionStatus === 'warning');

            setNeedEndUser(!order.endUser?.companyName);
            setNeedEndUserMessage(!order.endUser?.companyName ? 'Missing required information' : undefined);
            setNeedFulfillment(!order.shipTo?.companyName);
            setNeedFulfillmentMessage(!order.shipTo?.companyName ? 'Missing required information' : undefined);
        }
    }, [order, questions.data])

    React.useEffect(() => {
        setOrderable(!needOrderInfo && !needVendorQuestions && !needEndUser && !needFulfillment && order?.status === ORDER_STATUSES.PENDING);
    }, [needOrderInfo, needVendorQuestions, needEndUser, needFulfillment, order?.status]);

    React.useEffect(() => {
        setOrdered(order?.status !== ORDER_STATUSES.PENDING);
    }, [order?.status]);

    const validateVendorQuestions = (): 'error' | 'warning' | 'success' => {
        if (!questions.data) return 'success';
        let hasWarning = false;
        for (const question of questions.data.orderQuestions) {
            const answer = order?.answers?.find(a => a.id === question.id);
            if (answer?.value != null && answer.value !== '') continue;
            if (question.isRequired) return 'error';
            hasWarning = true;
        }
        for (const item of order?.items ?? []) {
            const itemQuestions = questions.data.itemQuestions[item.id] ?? [];
            for (const question of itemQuestions) {
                const answer = item.answers?.find(a => a.id === question.id);
                if (answer?.value != null && answer.value !== '') continue;
                if (question.isRequired) return 'error';
                hasWarning = true;
            }
        }
        return hasWarning ? 'warning' : 'success';
    }



    const handleItemAnswerChange = (itemId: string, questionId: string, value: string) => {
        setItemAnswers((prev) => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                [questionId]: value
            }
        }));
        debouncedSave();
    }

    const handleOrderAnswerChange = (questionId: string, value: string) => {
        setOrder((prev) => {
            if (!prev) return prev;
            const existingAnswerIndex = prev.answers?.findIndex(a => a.id === questionId) ?? -1;
            let updatedAnswers: IProcurementRequirementAnswer[] | undefined = prev.answers;
            if (existingAnswerIndex >= 0) {
                updatedAnswers = [...(prev.answers ?? [])];
                updatedAnswers[existingAnswerIndex] = { id: questionId, value };
            } else {
                updatedAnswers = [...(prev.answers ?? []), { id: questionId, value }];
            }
            return {
                ...prev,
                answers: updatedAnswers
            }
        });
        setUpdateOrderData((data) => {
            const existing = data.answers ?? [];
            const idx = existing.findIndex(a => a.id === questionId);
            const updated = idx >= 0
                ? existing.map((a, i) => i === idx ? { id: questionId, value } : a)
                : [...existing, { id: questionId, value }];
            return { ...data, answers: updated };
        });
        debouncedSave();
    }

    const toggleBox = (expanded: boolean, itemKey?: string) => {
        if (itemKey === undefined) return;
        if (!expanded) setCurrentStep(STEP_NONE);
        else setCurrentStep(itemKey);
    }

    const placeOrder = () => {
        if (!order) return;
        setOrdering(true);
        procure({
            orderId: order.id,
            data: {}
         }).unwrap().then(() => {
            setOrderError(undefined);
            setOrdering(false);
            props.onClose();
        }).catch((err) => {
            setOrderError(err?.data?.message || 'Error placing order');
            setOrdering(false);
         });
    }


    const errorColor = 'rgb(255, 138, 138)';
    const warningColor = 'rgb(255, 255, 149)';

    return (
        <>
        <Panel
            isOpen={!!props.orderId}
            headerText={`Order Details ${order?.vendor ? `- ${order.vendor}` : ''}`}
            onRenderHeader={() => <div className='ms-Panel-header' style={{paddingLeft: '24px', paddingRight: '0.5em', alignSelf: 'flex-start', flexGrow: 1}}>
                <div className="ms-Panel-headerText">
                    <Stack horizontal verticalAlign='center'>
                        <StackItem>
                            <span style={{
                                fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
                                fontSize: '20px',
                                fontWeight: 600,
                                color: 'rgb(50, 49, 48)',
                                lineHeight: '27px',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                hyphens: 'auto'
                            }}>
                                Order Details {order?.vendor ? `- ${order.vendor}` : ''}
                            </span>
                        </StackItem>
                        {updating && <Spinner styles={{ root: { marginLeft: '1em' }}} size={SpinnerSize.small} label='Saving' labelPosition='right' />}
                        {order && <StackItem styles={{ root: { marginLeft: 'auto' }}}>
                            <IconButton
                                iconProps={{ iconName: 'MoreVertical' }}
                                title='More actions'
                                ariaLabel='More actions'
                                onRenderMenuIcon={() => null}
                                menuProps={{
                                    items: [
                                        ...(hasProcurementExtension ? [
                                            {
                                                key: 'fetchFromVendor',
                                                text: fetchingFromVendor ? 'Updating...' : 'Update from Vendor',
                                                iconProps: { iconName: 'Refresh' },
                                                disabled: fetchingFromVendor || order.status === ORDER_STATUSES.PENDING,
                                                onClick: handleFetchFromVendor,
                                            },
                                        ] : []),
                                        ...(order.crmPurchaseOrders ?? []).map(poRef => ({
                                            key: `openCrmPo-${poRef.folioId}-${poRef.purchaseOrderId}`,
                                            text: `Open Purchase Order in ${poRef.folioName ?? 'CRM'}`,
                                            iconProps: { iconName: 'OpenInNewWindow' },
                                            onClick: () => { void handleOpenCrmPurchaseOrder(poRef); },
                                        })),
                                        ...((hasProcurementExtension && !(order.crmPurchaseOrders?.length)) ? [
                                            {
                                                key: 'createCrmPo',
                                                text: creatingCrmPo ? 'Creating...' : 'Create Purchase Order',
                                                iconProps: { iconName: 'Add' },
                                                disabled: creatingCrmPo,
                                                onClick: handleCreateCrmPurchaseOrder,
                                            },
                                        ] : []),
                                        {
                                            key: 'csv',
                                            text: generatingCsv ? 'Generating...' : 'Export as CSV',
                                            iconProps: { iconName: 'Download' },
                                            disabled: generatingCsv,
                                            onClick: handleGenerateCsv,
                                        },
                                        { key: 'divider', itemType: 1 },
                                        {
                                            key: 'delete',
                                            text: 'Delete Order',
                                            iconProps: { iconName: 'Delete', style: { color: '#d13438' } },
                                            style: { color: '#d13438' },
                                            onClick: () => setShowDeleteConfirm(true),
                                        },
                                    ]
                                }}
                                />
                        </StackItem>}
                    </Stack>
                </div>
            </div>}
            onDismiss={props.onClose}
            onOuterClick={(ev) => {
                // react-toastify renders toasts outside the panel's DOM, so Fluent's blocking-panel
                // outer-click handling treats clicking a toast (e.g. dismissing the CRM purchase-order
                // error) as a click outside and would close the panel. Ignore clicks that land on a
                // toast; any other outside click still dismisses the panel as before.
                if ((ev?.target as HTMLElement | null)?.closest?.('.Toastify')) return;
                props.onClose();
            }}
            type={PanelType.medium}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    {csvError && <MessageBar messageBarType={MessageBarType.error}>{csvError}</MessageBar>}
                    {csvUrl && <MessageBar messageBarType={MessageBarType.success}>
                        CSV ready: <a href={csvUrl} target='_blank' rel='noreferrer'>Download</a>
                    </MessageBar>}
                    {statusError && <MessageBar messageBarType={MessageBarType.error}>{statusError}</MessageBar>}
                    {updateOrderError && <MessageBar messageBarType={MessageBarType.error}>{updateOrderError}</MessageBar>}
                    {orderError && <MessageBar messageBarType={MessageBarType.error}>{orderError}</MessageBar>}
                    {fetchFromVendorError && <MessageBar messageBarType={MessageBarType.error}>{fetchFromVendorError}</MessageBar>}
                    {fetchFromVendorSuccess && <MessageBar messageBarType={MessageBarType.success}>Order updated from vendor.</MessageBar>}
                    {deleteError && <MessageBar messageBarType={MessageBarType.error}>{deleteError}</MessageBar>}
                    <Stack horizontal tokens={{ childrenGap: '1em' }}>
                        <DefaultButton onClick={props.onClose} style={{flexGrow: 1}}>Close</DefaultButton>
                        {hasProcurementExtension
                            ? <DefaultButton primary disabled={ordering || !orderable} onClick={placeOrder} style={{flexGrow:1}}>Order</DefaultButton>
                            : <DefaultButton primary disabled={generatingCsv} onClick={handleGenerateCsv} style={{flexGrow:1}}>Export as CSV</DefaultButton>
                        }
                    </Stack>
                </Stack>
            )}
        >
            {order != null ?
                <div>
                    <Pivot>
                        <PivotItem headerText='Order' key='placeOrder'>
                            <AccordionBox
                                title={STEP_ORDER_INFO}
                                secondaryTitle={order?.name}
                                isExpanded={currentStep === STEP_ORDER_INFO}
                                onToggle={toggleBox}
                                itemKey={STEP_ORDER_INFO}
                                headerBackgroundColor={needOrderInfo ? errorColor : undefined}
                                tooltip={needOrderInfoMessage}
                                tooltipIcon='Warning'
                            >
                                {hasProcurementExtension
                                    ? <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.5em' }} style={{ marginBottom: '0.5em' }}>
                                        <Label>Status:</Label>
                                        <span>{order.status}</span>
                                    </Stack>
                                    : <>
                                        <Dropdown
                                            label='Status'
                                            selectedKey={order.status}
                                            options={statusOptions}
                                            onChange={handleStatusChange}
                                            disabled={updatingStatus}
                                            styles={{ root: { marginBottom: '0.5em' } }}
                                        />
                                        {statusError && <MessageBar messageBarType={MessageBarType.error}>{statusError}</MessageBar>}
                                    </>}
                                <DataField
                                    label='Order Name'
                                    value={updateOrderData?.name === undefined ? order?.name || '' : updateOrderData.name}
                                    onChange={(e, val) => {
                                        setUpdateOrderData((data) => ({ ...data, name: val }));
                                        debouncedSave();
                                    }}
                                />
                                <DataField
                                    label='Purchase Order Number'
                                    value={updateOrderData?.purchaseOrderNumber === undefined ? order?.purchaseOrderNumber || '' : updateOrderData.purchaseOrderNumber}
                                    onChange={(e,val) => {
                                        setUpdateOrderData((data) => ({
                                            ...data,
                                            purchaseOrderNumber: val
                                        }));
                                        debouncedSave();
                                    }}
                                    disabled={ordered}
                                />
                                <DataField
                                    multiline
                                    label='Internal Order Notes'
                                    toolTip='These notes are for internal use only and are not sent to the vendor'
                                    toolTipLocation='right'
                                    value={updateOrderData?.internalOrderNotes === undefined ? order?.internalOrderNotes || '' : updateOrderData.internalOrderNotes}
                                    onChange={(e, val) => {
                                        setUpdateOrderData((data) => ({ ...data, internalOrderNotes: val }));
                                        debouncedSave();
                                    }}
                                />
                            </AccordionBox>
                            <AccordionBox
                                title={STEP_PRODUCTS}
                                secondaryTitle={`${order?.items.length || 0} Items`}
                                isExpanded={currentStep === STEP_PRODUCTS}
                                onToggle={toggleBox}
                                itemKey={STEP_PRODUCTS}
                            >
                                <OrderItemsTable order={order} onChange={(updatedOrder) => setOrder(updatedOrder)} />
                            </AccordionBox>
                            <AccordionBox
                                title={STEP_VENDOR_QUESTIONS}
                                isExpanded={currentStep === STEP_VENDOR_QUESTIONS}
                                onToggle={toggleBox}
                                itemKey={STEP_VENDOR_QUESTIONS}
                                headerBackgroundColor={needVendorQuestions ? errorColor : warnVendorQuestions ? warningColor : undefined}
                                tooltip={needVendorQuestionsMessage}
                                tooltipIcon='Warning'
                            >
                                <DataField
                                    multiline
                                    label='Vendor Notes'
                                    toolTip='These notes are sent to supported vendors as "Order Notes"'
                                    toolTipLocation='right'
                                    value={updateOrderData?.vendorNotes === undefined ? order?.vendorNotes || '' : updateOrderData.vendorNotes}
                                    onChange={(e, val) => {
                                        setUpdateOrderData((data) => ({ ...data, vendorNotes: val }));
                                        debouncedSave();
                                    }}
                                    disabled={ordered}
                                />
                                <DataField
                                    label='Requested Carrier'
                                    value={updateOrderData?.requestedCarrier === undefined ? order?.requestedCarrier || '' : updateOrderData.requestedCarrier}
                                    disabled={ordered}
                                    onChange={(e, val) => {
                                        setUpdateOrderData((data) => ({ ...data, requestedCarrier: val }));
                                        debouncedSave();
                                    }}
                                />
                                <DataField
                                    label='Requested Warehouse'
                                    disabled={ordered}
                                    value={updateOrderData?.requestedWarehouse === undefined ? order?.requestedWarehouse || '' : updateOrderData.requestedWarehouse}
                                    onChange={(e, val) => {
                                        setUpdateOrderData((data) => ({ ...data, requestedWarehouse: val }));
                                        debouncedSave();
                                    }}
                                />
                                <ToggleField
                                    label='Allow Backorder'
                                    inline
                                    checked={updateOrderData?.allowBackorder === undefined ? order?.allowBackorder || false : updateOrderData.allowBackorder}
                                    onChange={(e, checked) => {
                                        setUpdateOrderData((data) => ({ ...data, allowBackorder: checked }));
                                        debouncedSave();
                                    }}
                                    disabled={ordered}
                                />
                                <QuestionsForm
                                    questions={questions.data}
                                    isFetching={questions.isFetching}
                                    order={order}
                                    onItemAnswerChange={handleItemAnswerChange}
                                    itemAnswers={itemAnswers}
                                    onOrderAnswerChange={handleOrderAnswerChange}
                                    disabled={ordered}
                                />
                            </AccordionBox>
                            <AccordionBox
                                title={STEP_END_USER}
                                secondaryTitle={order?.endUser?.companyName}
                                isExpanded={currentStep === STEP_END_USER}
                                onToggle={toggleBox}
                                itemKey={STEP_END_USER}
                                headerBackgroundColor={needEndUser ? warningColor : undefined}
                                tooltip={needEndUserMessage}
                                tooltipIcon='Warning'
                            >
                                <CompanyForm
                                    company={order.endUser ?? {}}
                                    onChange={(updatedCompany) => {
                                        setOrder({ ...order, endUser: updatedCompany });
                                        setUpdateOrderData((data) => ({ ...data, endUser: updatedCompany }));
                                        debouncedSave();
                                    }}
                                    disabled={ordered}
                                />
                            </AccordionBox>
                            <AccordionBox
                                title={STEP_FULFILLMENT}
                                secondaryTitle={order?.shipTo?.companyName}
                                isExpanded={currentStep === STEP_FULFILLMENT}
                                onToggle={toggleBox}
                                itemKey={STEP_FULFILLMENT}
                                headerBackgroundColor={needFulfillment ? warningColor : undefined}
                                tooltip={needFulfillmentMessage}
                                tooltipIcon='Warning'
                            >
                                <ToggleField
                                    label='Dropship'
                                    inline
                                    checked={updateOrderData?.dropship === undefined ? order?.dropship || false : updateOrderData.dropship}
                                    onChange={(e, checked) => {
                                        setUpdateOrderData((data) => ({ ...data, dropship: checked }));
                                        debouncedSave();
                                    }}
                                />
                                {!(updateOrderData?.dropship ?? order?.dropship) &&
                                    <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: '0.5em' } }}>
                                        Only contacts marked as a procurement shipping contact can be selected. Enable Dropship to ship to any contact.
                                    </MessageBar>}
                                <CompanyForm
                                    company={order.shipTo ?? {}}
                                    onChange={(updatedCompany) => {
                                        setOrder({ ...order, shipTo: updatedCompany });
                                        setUpdateOrderData((data) => ({ ...data, shipTo: updatedCompany }));
                                        debouncedSave();
                                    }}
                                    disabled={ordered}
                                    restrictToProcurementContactType={(updateOrderData?.dropship ?? order?.dropship) ? undefined : 'shipping'}
                                />
                            </AccordionBox>
                        </PivotItem>
                        <PivotItem headerText='Activity' key='activity'>
                            <OrderActivities orderId={order.id} />
                        </PivotItem>
                    </Pivot>
                </div>
                : <Spinner
                    label='Loading'
                    style={{ marginTop: '3em' }}
                    styles={{
                        circle: {
                            width: '90px',
                            height: '90px',
                            borderWidth: '6px'
                        }
                    }}
                />
            }
        </Panel>
        <Dialog
            hidden={!showDeleteConfirm}
            onDismiss={() => !deleting && setShowDeleteConfirm(false)}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Delete Order',
                subText: `Are you sure you want to delete the order "${order?.name || order?.id}"? This cannot be undone.`,
            }}
            modalProps={{ isBlocking: true }}
        >
            {deleteError && <MessageBar messageBarType={MessageBarType.error} style={{ marginBottom: '0.5em' }}>{deleteError}</MessageBar>}
            <DialogFooter>
                <PrimaryButton
                    onClick={handleDeleteOrder}
                    disabled={deleting}
                    styles={{ root: { backgroundColor: '#d13438', borderColor: '#d13438' } }}
                >
                    {deleting ? 'Deleting...' : 'Delete'}
                </PrimaryButton>
                <DefaultButton onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</DefaultButton>
            </DialogFooter>
        </Dialog>
        </>
    );
}

interface IQuestionsFormProps {
    questions?: IGetProcurementQuestionResponse;
    isFetching: boolean;
    order?: IProcurementOrder;
    itemAnswers?: {[itemId: string]: {[questionId: string]: string}};
    onOrderAnswerChange: (questionId: string, value: string) => void;
    onItemAnswerChange: (itemId: string, questionId: string, value: string) => void;
    disabled?: boolean;
}

const QuestionsForm: React.FC<IQuestionsFormProps> = (props) => {
    if (props.isFetching || !props.questions) {
        return <Spinner label='Loading vendor questions...' />;
    }

    if (
        (props.questions?.itemQuestions == null || Object.keys(props.questions.itemQuestions)?.length === 0) &&
        (props.questions?.orderQuestions == null || props.questions.orderQuestions.length === 0)
    ) {
        return <div>No additional vendor questions for this order.</div>;
    }

    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            {Object.entries(props.questions.orderQuestions ?? {}).length > 0 && <>
                <h3 style={{marginTop: 0}}>Order Questions</h3>
                {props.questions.orderQuestions.map((question) => (
                    <QuestionField
                        key={question.id}
                        question={question}
                        value={props.order?.answers?.find(a => a.id === question.id)?.value || ''}
                        onChange={props.onOrderAnswerChange}
                        disabled={props.disabled}
                    />
                ))}
            </>}
            {Object.entries(props.questions.itemQuestions ?? {}).map(([itemId, itemQuestions]) => {
                if (itemQuestions.length === 0) return null;
                return (
                    <div key={itemId}>
                        <Separator />
                        <h3 style={{marginTop: 0}}>Questions for {props.order?.items.find(i => i.id === itemId)?.productName ?? 'Unknown Item'}</h3>
                        {itemQuestions.map((question) => (
                            <QuestionField
                                key={question.id}
                                question={question}
                                value={props.itemAnswers?.[itemId]?.[question.id] || ''}
                                onChange={(questionId, value) => props.onItemAnswerChange(itemId, questionId, value)}
                                disabled={props.disabled}
                            />
                        ))}
                    </div>
                )
            })}
        </Stack>
    );
}

interface IQuestionFieldProps {
    question: IProcurementQuestion;
    value: string;
    onChange: (questionId: string, value: string) => void;
    disabled?: boolean;
}

const QuestionField: React.FC<IQuestionFieldProps> = (props) => {
    if (props.question.dataType === 'boolean') {
        return (
            <Dropdown
                label={props.question.name}
                selectedKey={props.value}
                onChange={(e, option) => props.onChange(props.question.id, option?.key as string)}
                options={[
                    { key: 'true', text: 'Yes' },
                    { key: 'false', text: 'No' }
                ]}
                placeholder='Select an option'
                ariaLabel={props.question.name}
                disabled={props.disabled}
            />
        );
    }
    if (props.question.options && props.question.options.length > 0) {
        return (
            <Dropdown
                label={props.question.name}
                selectedKey={props.value}
                onChange={(e, option) => props.onChange(props.question.id, option?.key as string)}
                options={props.question.options.map(option => ({ key: option, text: option }))}
                disabled={props.disabled}
                placeholder='Select an option'
                ariaLabel={props.question.name}
            />
        );
    }
    return (
        <DataField
            required={props.question.isRequired}
            label={props.question.name}
            toolTip={props.question.description}
            value={props.value}
            onChange={(e, newValue) => props.onChange(props.question.id, newValue ?? '')}
            disabled={props.disabled}
        />
    );
}

export default ExistingOrderPanel;